import React, { useState } from 'react';
import { Container, Row, Col, ListGroup, Accordion, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faClipboardList, faDownload } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import AppNav from '../../header/AppNav';
import { AshaServices } from '../../services/AshaService';
import { AshaAbstractReportService } from '../../services/AshaAbstractReportService';

const Reports = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportType, setReportType] = useState('tillToday');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateError, setDateError] = useState('');

  // Get today's date in YYYY-MM-DD format for validation
  const today = new Date().toISOString().split('T')[0];

  const validateDates = () => {
    setDateError('');
    
    if (reportType === 'customDate') {
      if (!fromDate || !toDate) {
        setDateError('Please select both from and to dates');
        return false;
      }
      
      if (fromDate > today) {
        setDateError('From date cannot be greater than today');
        return false;
      }
      
      if (toDate > today) {
        setDateError('To date cannot be greater than today');
        return false;
      }
      
      if (fromDate > toDate) {
        setDateError('To date cannot be less than from date');
        return false;
      }
    }
    
    return true;
  };

  const downloadAshaDailyReport = async () => {
    if (!validateDates()) {
      return;
    }
    
    setIsLoading(true);
    setError('');
    try {
      let response;
      console.log(reportType);
      if (reportType === 'tillToday') {
        response = await AshaServices.getAshaDailyReport();
      } else {
        response = await AshaServices.getAshaDailyReportByDateRange(fromDate, toDate);
      }
  
      if (response && response.data) {

        const mappedData = response.data.map(item => ({
          Taluk: item.taluk,
          "PHC Name": item.phcName,
          "Asha Block": item.ashaBlock,
          "Asha Village": item.ashaVillage,
          "Asha Name": item.ashaName,
          "Asha Mobile No": item.ashaMobileNo,
          "Total Population": item.totalPopulation,
          "Population Screened": item.actualTotalMembersScreened,
          "No.Of Houses Alloted": item.noOfHousesAlloted,
          "Actual House Visited(Synched)": item.actualHouseVisited,
          "No.of Patients Identified": item.numberOfPersonsWithEyeProblem,
          "No.of Patients (Synched)": item.actualTotalPatientRefferedForSecondaryScreen
        }));
  

        const wb = XLSX.utils.book_new();
  

        const ws = XLSX.utils.json_to_sheet(mappedData);

        const headerKeys = Object.keys(mappedData[0] || {});
        const colWidths = headerKeys.map(key => ({
          wch: Math.max(
            key.length, 
            ...mappedData.map(row => (row[key] ? row[key].toString().length : 0))
          ) + 2
        }));
        ws['!cols'] = colWidths;
  

        XLSX.utils.book_append_sheet(wb, ws, 'DailyAshaReport');
  
 
        const now = new Date();
        const currentDate = `${String(now.getDate()).padStart(2, '0')}-${String(
          now.getMonth() + 1
        ).padStart(2, '0')}-${now.getFullYear()}`;
        let fileName = '';
        if (reportType === 'tillToday') {
          fileName = `Asha_Report_${currentDate}.xlsx`;
        } else {
          const formattedFrom = formatDate(fromDate);
          const formattedTo = formatDate(toDate);
          fileName = `Asha_Report_${formattedFrom}_to_${formattedTo}.xlsx`;
        }
 
        XLSX.writeFile(wb, fileName);
        setFromDate('');
        setToDate('');
      } else {
        setError('No data available for the report');
      }
    } catch (err) {
      console.error('Error downloading report:', err);
      setError('Failed to download report. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadAshaAbstractReport = async () => {
    setIsLoading(true);
    setError('');
    try {
      let response;
      response = await AshaAbstractReportService.getAll();
      
      if (response) {
        const mappedData = response.map(item => ({
          Taluk: item.ashaDetails.ashaBlockDetails.phcDetails.talukDetails.taluk,
          "PHC Name": item.ashaDetails.ashaBlockDetails.phcDetails.phc,
          "Asha Block": item.ashaDetails.ashaBlockDetails.ashaBlock,
          "Asha Village": item.ashaDetails.village,
          "Asha Name": item.ashaDetails.name,
          "Asha Mobile No": item.ashaDetails.mobileNo,
          "Total No.of Houses": item.totalHouses,
          "No.Of Houses Screened": item.housesScreened,
          "Total Population": item.totalPopulation,
          "No.of Persons Screened": item.personsScreened,
          "No. of persons above 40 years screened": item.personsAbove40Screened,
          "No. of persons with Diabetes screened": item.personsWithDiabetesScreened,
          "No. of persons referred to Secondary camp": item.personsReferredToSecondaryCamp
        }));
  

        const wb = XLSX.utils.book_new();
  

        const ws = XLSX.utils.json_to_sheet(mappedData);

        const headerKeys = Object.keys(mappedData[0] || {});
        const colWidths = headerKeys.map(key => ({
          wch: Math.max(
            key.length, 
            ...mappedData.map(row => (row[key] ? row[key].toString().length : 0))
          ) + 2
        }));
        ws['!cols'] = colWidths;
  

        XLSX.utils.book_append_sheet(wb, ws, 'AshaAbstractReport');
  
 
        const now = new Date();
        const currentDate = `${String(now.getDate()).padStart(2, '0')}-${String(
          now.getMonth() + 1
        ).padStart(2, '0')}-${now.getFullYear()}`;
        let fileName = `Asha_Abstract_Report_as_on_${currentDate}.xlsx`;
        XLSX.writeFile(wb, fileName);
        setFromDate('');
        setToDate('');
      } else {
        setError('No data available for the report');
      }
    } catch (err) {
      console.error('Error downloading report:', err);
      setError('Failed to download report. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };
  return (
    <div>
      <AppNav />
      <Container className="mt-4">
        <h2 className="mb-4 text-center">Select Report Type</h2>

        <Row>
          {/* Left Column - Daily Asha Report */}
          <Col md={6}>
            <ListGroup>
              <ListGroup.Item className="p-0 border-0">
                <Accordion>
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>
                      <FontAwesomeIcon icon={faFileExcel} className="text-primary me-2" />
                      Daily Asha Report
                    </Accordion.Header>
                    <Accordion.Body>
                      {/* <p className="mb-3">Download the Daily Asha Report in Excel format.</p> */}
                      <p className="mb-3">Download the Abstract Asha Report in Excel format.</p>
                      {/* Radio buttons for report type selection */}
                      <Form.Group className="mb-3">
                        {/* <Form.Label className="fw-bold">Select Report Type:</Form.Label> */}
                        <Row className="mt-2">
                          {/* <Col md={6}>
                            <Form.Check
                              type="radio"
                              id="tillToday"
                              name="reportType"
                              label="Till Today"
                              checked={reportType === 'tillToday'}
                              onChange={(e) => {
                                setReportType('tillToday');
                                setDateError('');
                              }}
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Check
                              type="radio"
                              id="customDate"
                              name="reportType"
                              label="Custom Date"
                              checked={reportType === 'customDate'}
                              onChange={(e) => {
                                setReportType('customDate');
                                setDateError('');
                              }}
                            />
                          </Col> */}
                        </Row>
                      </Form.Group>

                      {/* Date pickers for custom date selection */}
                      {reportType === 'customDate' && (
  <Row className="mb-3">
    <Col md={6}>
      <Form.Group>
        <Form.Label className="fw-bold">From Date:</Form.Label>
        <Form.Control
          type="date"
          value={fromDate}
          max={today}
          onChange={(e) => {
            setFromDate(e.target.value);
            setDateError('');
          }}
          className="mb-2"
        />
      </Form.Group>
    </Col>
    <Col md={6}>
      <Form.Group>
        <Form.Label className="fw-bold">To Date:</Form.Label>
        <Form.Control
          type="date"
          value={toDate}
          max={today}
          min={fromDate || ''}   
          onChange={(e) => {
            setToDate(e.target.value);
            setDateError('');
          }}
          className="mb-2"
        />
      </Form.Group>
    </Col>
  </Row>
)}

{dateError && (
  <div className="mb-3" style={{ color: 'red', fontWeight: '500' }}>
    {dateError}
  </div>
)}

                      <Button
                        variant="primary"
                        onClick={downloadAshaAbstractReport}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faDownload} className="me-2" />
                            Download Excel
                          </>
                        )}
                      </Button>
                      {error && (
                        <Alert variant="danger" className="mt-2">
                          {error}
                        </Alert>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </ListGroup.Item>
            </ListGroup>
          </Col>

          {/* Right Column - Screening Report */}
          <Col md={6}>
            <ListGroup>
              <ListGroup.Item className="p-0 border-0">
                <Accordion>
                  <Accordion.Item eventKey="1">
                    <Accordion.Header>
                      <FontAwesomeIcon icon={faClipboardList} className="text-success me-2" />
                      Screening Report
                    </Accordion.Header>
                    <Accordion.Body>
                      <ul className="list-unstyled ms-3">
                        <li
                          className="mb-2 text-primary"
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate('/screening-report/by-camp')}
                        >
                          Primary Screening Report
                        </li>
                        <li
                          className="mb-2 text-primary"
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate('/secondary-screening-report')}
                        >
                          Secondary Screening Report
                        </li>
                      </ul>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </ListGroup.Item>
            </ListGroup>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Reports;
